import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
  Chip,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';
import { getElevenLabsVoices } from '../../services/api';

// Provider configuration (same as in TherapistVoiceSettingsV2)
const PROVIDERS = {
  elevenlabs: {
    name: 'ElevenLabs',
    icon: '🎙️',
    color: '#1a1a1a'
  },
  google: {
    name: 'Google Cloud',
    icon: '🔊',
    color: '#4285f4'
  },
  azure: {
    name: 'Azure',
    icon: '☁️',
    color: '#0078d4'
  },
  openai: {
    name: 'OpenAI',
    icon: '🤖',
    color: '#10a37f'
  }
};

// Available voices per provider
const PROVIDER_VOICES = {
  elevenlabs: [], // Will be loaded dynamically
  google: [
    { voice_id: 'en-GB-Neural2-A', name: 'British Female A' },
    { voice_id: 'en-GB-Neural2-F', name: 'British Female F' },
    { voice_id: 'en-GB-Neural2-C', name: 'British Female C' },
    { voice_id: 'en-GB-Neural2-B', name: 'British Male B' },
    { voice_id: 'en-GB-Neural2-D', name: 'British Male D' }
  ],
  azure: [
    { voice_id: 'en-GB-LibbyNeural', name: 'Libby' },
    { voice_id: 'en-GB-SoniaNeural', name: 'Sonia' },
    { voice_id: 'en-GB-RyanNeural', name: 'Ryan' }
  ],
  openai: [
    { voice_id: 'alloy', name: 'Alloy' },
    { voice_id: 'nova', name: 'Nova' },
    { voice_id: 'onyx', name: 'Onyx' },
    { voice_id: 'echo', name: 'Echo' }
  ]
};

const ClientVoiceOverrides = ({ token, clientId, clientName }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  const [voiceSettings, setVoiceSettings] = useState({
    overrides: { providers: {} },
    therapistDefaults: { providers: {} },
    effectiveSettings: {},
    allowedProviders: []
  });
  
  const [availableVoices, setAvailableVoices] = useState(PROVIDER_VOICES);
  const [modifiedProviders, setModifiedProviders] = useState(new Set());

  useEffect(() => {
    if (token && clientId) {
      fetchVoiceSettings();
    }
  }, [token, clientId]);

  const fetchVoiceSettings = async () => {
    setLoading(true);
    setError('');
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      
      // Fetch client-specific voice settings
      const response = await axios.get(
        `${API_BASE_URL}/therapist/client/${clientId}/voice-settings/v2`,
        {
          headers: { 'x-auth-token': token }
        }
      );
      
      // Extract allowed providers from therapist defaults
      // Use the allowedProviders field if available, otherwise fall back to providers keys
      const allowedProviders = response.data.therapistDefaults.allowedProviders || 
                               Object.keys(response.data.therapistDefaults.providers || {}) || 
                               ['elevenlabs'];
      
      setVoiceSettings({
        ...response.data,
        allowedProviders
      });
      
      // Load ElevenLabs voices
      try {
        const elevenLabsVoices = await getElevenLabsVoices();
        setAvailableVoices(prev => ({
          ...prev,
          elevenlabs: elevenLabsVoices
        }));
      } catch (err) {
        console.warn('Could not load ElevenLabs voices:', err);
      }
      
    } catch (err) {
      console.error('Error fetching client voice settings:', err);
      setError('Failed to load voice settings');
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceChange = (provider, role, value) => {
    setVoiceSettings(prev => ({
      ...prev,
      overrides: {
        ...prev.overrides,
        providers: {
          ...prev.overrides.providers,
          [provider]: {
            ...prev.overrides.providers[provider],
            [role]: value === '' ? null : value
          }
        }
      }
    }));
    
    setModifiedProviders(prev => new Set([...prev, provider]));
  };

  const handleSaveProvider = async (provider) => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const overrides = voiceSettings.overrides.providers[provider] || {};
      
      await axios.put(
        `${API_BASE_URL}/therapist/client/${clientId}/voice-settings/v2/provider/${provider}`,
        {
          aiTherapistVoice: overrides.aiTherapistVoice,
          humanTherapistVoice: overrides.humanTherapistVoice,
          clientVoice: overrides.clientVoice,
          useDefaults: !overrides.aiTherapistVoice && !overrides.humanTherapistVoice && !overrides.clientVoice
        },
        {
          headers: { 'x-auth-token': token }
        }
      );
      
      setSuccess(`${PROVIDERS[provider].name} voice overrides saved for ${clientName}`);
      setModifiedProviders(prev => {
        const newSet = new Set(prev);
        newSet.delete(provider);
        return newSet;
      });
      
      // Refresh to get updated effective settings
      await fetchVoiceSettings();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving client voice overrides:', err);
      setError(err.response?.data?.msg || 'Failed to save voice overrides');
    } finally {
      setSaving(false);
    }
  };

  const handleResetProvider = (provider) => {
    setVoiceSettings(prev => ({
      ...prev,
      overrides: {
        ...prev.overrides,
        providers: {
          ...prev.overrides.providers,
          [provider]: {
            aiTherapistVoice: null,
            humanTherapistVoice: null,
            clientVoice: null
          }
        }
      }
    }));
    
    setModifiedProviders(prev => new Set([...prev, provider]));
  };

  const renderProviderTab = (provider) => {
    const overrides = voiceSettings.overrides.providers[provider] || {};
    const defaults = voiceSettings.therapistDefaults.providers[provider] || {};
    const effective = voiceSettings.effectiveSettings[provider] || {};
    const voices = availableVoices[provider] || [];
    const hasChanges = modifiedProviders.has(provider);
    
    const getRoleLabel = (role) => {
      const labels = {
        aiTherapistVoice: 'AI Therapist Voice',
        humanTherapistVoice: 'Your Voice in Transcripts',
        clientVoice: 'Client Voice (Playback)'
      };
      return labels[role] || role;
    };
    
    return (
      <Paper elevation={0} variant="outlined" sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">
              {PROVIDERS[provider].icon} {PROVIDERS[provider].name} Overrides
            </Typography>
            {hasChanges && (
              <Chip label="Unsaved" size="small" color="warning" />
            )}
          </Box>
          <Box>
            <Tooltip title="Reset to therapist defaults">
              <IconButton onClick={() => handleResetProvider(provider)} disabled={saving} aria-label="Reset to therapist defaults">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => handleSaveProvider(provider)}
              disabled={saving || !hasChanges}
              size="small"
            >
              Save
            </Button>
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          {['aiTherapistVoice', 'humanTherapistVoice', 'clientVoice'].map(role => (
            <Grid item xs={12} key={role}>
              <FormControl fullWidth size="small">
                <InputLabel>{getRoleLabel(role)}</InputLabel>
                <Select
                  value={overrides[role] || ''}
                  onChange={(e) => handleVoiceChange(provider, role, e.target.value)}
                  label={getRoleLabel(role)}
                >
                  <MenuItem value="">
                    <em>Use Therapist Default ({defaults[role === 'aiTherapistVoice' ? 'defaultAITherapistVoice' : 
                                                     role === 'humanTherapistVoice' ? 'defaultHumanTherapistVoice' : 
                                                     'defaultClientVoice'] || 'Not Set'})</em>
                  </MenuItem>
                  {voices.map(voice => (
                    <MenuItem key={voice.voice_id} value={voice.voice_id}>
                      {voice.name}
                      {voice.voice_id === effective[role.replace('Voice', '')] && 
                        <Chip label="Current" size="small" sx={{ ml: 1 }} />
                      }
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Show what will actually be used */}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Effective: {effective[role.replace('Voice', '')] || defaults[role === 'aiTherapistVoice' ? 'defaultAITherapistVoice' : 
                                                                             role === 'humanTherapistVoice' ? 'defaultHumanTherapistVoice' : 
                                                                             'defaultClientVoice'] || 'Platform Default'}
                {effective.isUsingDefaults?.[role.replace('Voice', '')] && ' (Using therapist default)'}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const allowedProviders = voiceSettings.allowedProviders || ['elevenlabs'];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Voice Overrides for {clientName}
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Override your default voice settings for this specific client. Leave empty to use your therapist defaults.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          {allowedProviders.map((provider, index) => (
            <Tab
              key={provider}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{PROVIDERS[provider]?.icon}</span>
                  <span>{PROVIDERS[provider]?.name}</span>
                  {modifiedProviders.has(provider) && 
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', ml: 0.5 }} />
                  }
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>
      
      {allowedProviders.map((provider, index) => (
        <Box
          key={provider}
          hidden={activeTab !== index}
          sx={{ mt: 2 }}
        >
          {renderProviderTab(provider)}
        </Box>
      ))}
      
      <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <InfoIcon fontSize="small" />
          <Typography variant="caption">
            Client-specific overrides take priority over your default settings. The client will hear the AI therapist voice during sessions.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ClientVoiceOverrides;